import { Workflow } from "../../workflow/service/Workflow";
import { Budget } from "./Budget";

export class BudgetUtil {
    /**
     * Get status information for display
     */
    public static getStatusInfo(status?: Workflow.WorkflowStatus): { label: string; class: string } {
        if (!status) {
            return { label: "Draft", class: "draft" };
        }

        switch (status) {
            case "PENDING":
                return { label: "Awaiting Approval", class: "awaiting-approval" };
            case "IN_PROGRESS":
                return { label: "In Progress", class: "in-progress" };
            case "COMPLETED":
                return { label: "Approved", class: "approved" };
            case "DENIED":
                return { label: "Denied", class: "denied" };
            case "REQUESTED_CHANGES":
                return { label: "Changes Requested", class: "changes-requested" };
            default:
                return { label: "Unknown", class: "unknown" };
        }
    }

    /**
     * Extract numeric amount from BudgetPlan amount field which can be number or object
     */
    public static getAmount(plan: Budget.BudgetPlan): number {
        // Try allocatedAmount first
        if (typeof plan.allocatedAmount === "number") {
            return plan.allocatedAmount;
        }
        if (plan.allocatedAmount && typeof plan.allocatedAmount === "object" && "parsedValue" in plan.allocatedAmount) {
            return (plan.allocatedAmount as any).parsedValue;
        }

        // Fall back to amount field
        if (typeof plan.amount === "number") {
            return plan.amount;
        }
        if (plan.amount && typeof plan.amount === "object" && "parsedValue" in plan.amount) {
            return (plan.amount as any).parsedValue;
        }

        return 0;
    }

    /**
     * Check if a budget plan is in draft state
     */
    public static isDraft(plan: Budget.BudgetPlan): boolean {
        return !plan.workflowStatus;
    }

    /**
     * Check if a budget plan can be edited
     */
    public static canEdit(plan: Budget.BudgetPlan): boolean {
        return BudgetUtil.isDraft(plan) || plan.workflowStatus === "REQUESTED_CHANGES";
    }

    /**
     * Check if a budget plan has requested changes
     */
    public static hasRequestedChanges(plan: Budget.BudgetPlan): boolean {
        return plan.workflowStatus === "REQUESTED_CHANGES";
    }

    /**
     * Check if Submit for Approval should appear for a budget plan row.
     */
    public static shouldShowSubmitAction(plan: Budget.BudgetPlan, canManage: boolean): boolean {
        return canManage && BudgetUtil.isDraft(plan) && !!plan.workflowProcessId;
    }

    /**
     * Check if Resubmit should appear for a budget plan row.
     */
    public static shouldShowResubmitAction(plan: Budget.BudgetPlan, canManage: boolean): boolean {
        return canManage && BudgetUtil.hasRequestedChanges(plan) && !!plan.workflowProcessId;
    }

    /**
     * Check if a user can approve/deny a budget plan
     */
    public static canApprove(plan: Budget.BudgetPlan): boolean {
        return !!plan.todoId && (plan.workflowStatus === "PENDING" || plan.workflowStatus === "IN_PROGRESS");
    }
}
